import {
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  VolumeX,
  Volume2,
} from "lucide-react";
import { useRef, useState, useEffect } from "react";

export const MediaCarousel = ({
  mediaPreviewUrls,
  mediaMetadata,
  borderRadius,
}: {
  mediaPreviewUrls: string[];
  mediaMetadata: Array<{
    url: string;
    type: "image" | "video";
    duration?: number;
  }>;
  borderRadius: string;
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [mutedStates, setMutedStates] = useState<boolean[]>(
    mediaPreviewUrls.map(() => true)
  );
  const [playingStates, setPlayingStates] = useState<boolean[]>(
    mediaPreviewUrls.map(() => true)
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  // Update muted and playing states when media changes
  useEffect(() => {
    setMutedStates(mediaPreviewUrls.map(() => true));
    setPlayingStates(mediaPreviewUrls.map(() => true));
  }, [mediaPreviewUrls]);

  if (mediaPreviewUrls.length === 0) return null;

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -300,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 300,
        behavior: "smooth",
      });
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const itemWidth = scrollContainerRef.current.offsetWidth;
      const newIndex = Math.round(scrollLeft / itemWidth);

      if (newIndex !== currentIndex) {
        // Pause and mute previous videos
        videoRefs.current.forEach((video, idx) => {
          if (video && idx !== newIndex) {
            video.pause();
            video.muted = true;
          }
        });

        // Update states for previous videos
        setPlayingStates((prev) => {
          const newStates = [...prev];
          videoRefs.current.forEach((_, idx) => {
            if (idx !== newIndex) {
              newStates[idx] = false;
            }
          });
          return newStates;
        });

        // Play new video
        const newVideo = videoRefs.current[newIndex];
        if (newVideo) {
          newVideo.play().catch(() => {});
          setPlayingStates((prev) => {
            const newStates = [...prev];
            newStates[newIndex] = true;
            return newStates;
          });
        }

        setCurrentIndex(newIndex);
      }
    }
  };

  const toggleMute = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      video.muted = !video.muted;
      setMutedStates((prev) => {
        const newStates = [...prev];
        newStates[index] = video.muted;
        return newStates;
      });
    }
  };

  const togglePlay = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      if (video.paused) {
        video.play().catch(() => {});
        setPlayingStates((prev) => {
          const newStates = [...prev];
          newStates[index] = true;
          return newStates;
        });
      } else {
        video.pause();
        setPlayingStates((prev) => {
          const newStates = [...prev];
          newStates[index] = false;
          return newStates;
        });
      }
    }
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {mediaPreviewUrls.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              scrollLeft();
            }}
            style={{
              position: "absolute",
              left: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              background: "rgba(0,0,0,0.5)",
              border: "none",
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "white",
            }}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              scrollRight();
            }}
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              background: "rgba(0,0,0,0.5)",
              border: "none",
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "white",
            }}
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}
      <div
        ref={scrollContainerRef}
        className="media-carousel"
        onScroll={handleScroll}
        style={{
          display: "flex",
          width: "100%",
          height: "100%",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          borderRadius: borderRadius,
          gap: "10px",
          scrollbarWidth: "none", // Hide scrollbar for Firefox
          msOverflowStyle: "none", // Hide scrollbar for IE/Edge
        }}
      >
        <style>
          {`
          .media-carousel::-webkit-scrollbar {
            display: none;
          }
        `}
        </style>
        {mediaPreviewUrls.map((url, index) => {
          const media = mediaMetadata[index];
          const isVideo = media?.type === "video";

          return (
            <div
              key={url}
              style={{
                flex: "0 0 100%",
                width: "100%",
                height: "100%",
                scrollSnapAlign: "start",
                position: "relative",
              }}
            >
              {isVideo ? (
                <>
                  <video
                    key={url}
                    ref={(el) => {
                      videoRefs.current[index] = el;
                    }}
                    src={url}
                    autoPlay
                    loop
                    muted={mutedStates[index]}
                    playsInline
                    preload="auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlay(index);
                    }}
                    onCanPlay={(e) => {
                      const video = e.currentTarget;
                      video.play().catch((err) => {
                        console.log("Autoplay prevented:", err);
                      });
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                      cursor: "pointer",
                    }}
                  />
                  {/* Play/Pause Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlay(index);
                    }}
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      background: "rgba(0, 0, 0, 0.6)",
                      border: "none",
                      borderRadius: "50%",
                      width: "48px",
                      height: "48px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      zIndex: 5,
                      opacity: playingStates[index] ? 0 : 1,
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      if (playingStates[index]) {
                        e.currentTarget.style.opacity = "0";
                      }
                    }}
                  >
                    {playingStates[index] ? (
                      <Pause size={20} color="white" fill="white" />
                    ) : (
                      <Play size={20} color="white" fill="white" />
                    )}
                  </button>

                  {/* Mute/Unmute Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMute(index);
                    }}
                    style={{
                      position: "absolute",
                      bottom: 10,
                      right: 10,
                      background: "rgba(0, 0, 0, 0.6)",
                      border: "none",
                      borderRadius: "50%",
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      zIndex: 5,
                      color: "white",
                    }}
                  >
                    {mutedStates[index] ? (
                      <VolumeX size={16} />
                    ) : (
                      <Volume2 size={16} />
                    )}
                  </button>
                </>
              ) : (
                <img
                  src={url}
                  alt={`Media ${index}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              )}
              {mediaPreviewUrls.length > 1 && (
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    background: "rgba(0,0,0,0.6)",
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                  }}
                >
                  {index + 1}/{mediaPreviewUrls.length}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
